import { useEffect, useState } from "react";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";

import { Skeleton } from "antd";
import { DotChartOutlined } from "@ant-design/icons";

import { GOOGLE_MAP_KEY } from "../../../utils/configs";

import PlacesAutocomplete from "./places-auto-complete";

import HexLocationButton from "../../HexLocationButton";

import "./style.css";
import HexGoogleDistance from "../HexGoogleDistance";

const MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f6f4f1" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b6560" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f6f4f1" }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#ded6cb" }],
  },
  {
    featureType: "landscape.man_made",
    elementType: "geometry",
    stylers: [{ color: "#efece7" }],
  },
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#dfe7dc" }],
  },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#f7e5cd" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#ebd6b6" }],
  },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#cfe0e8" }] },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8ba3ae" }],
  },
];

const BASE_MAP_OPTIONS = {
  styles: MAP_STYLES,
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
};

const VIEW_MAP_OPTIONS = {
  ...BASE_MAP_OPTIONS,
  gestureHandling: "cooperative",
};

const EDIT_MAP_OPTIONS = {
  ...BASE_MAP_OPTIONS,
  gestureHandling: "greedy",
};

export default function GoogleMapPlaces(props) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries: ["places"],
  });

  if (!isLoaded)
    return (
      <Skeleton.Node active={true} className="p-1 full-width map-skeleton">
        <DotChartOutlined style={{ fontSize: 40, color: "#bfbfbf" }} />
      </Skeleton.Node>
    );
  return <Map {...props} />;
}

function Map(props) {
  const {
    initialViewPort,
    editable,
    onLocate,
    onMarkerChange,
    addressText,
    distance,
  } = props;

  const { latitude, longitude, zoom } = initialViewPort;

  const [isSelected, setSelected] = useState(false);
  const [center, setCenter] = useState({ lat: latitude, lng: longitude });

  useEffect(() => {
    setCenter({ lat: latitude, lng: longitude });
  }, [latitude, longitude]);

  const selectCenter = (center) => {
    const { addressText, ...rest } = center;
    setCenter(rest);
    setSelected(true);
    onMarkerChange({ lngLat: { ...center }, addressText });
  };

  const onMapClick = ({ latLng }) => {
    onMarkerChange({ lngLat: { lat: latLng.lat(), lng: latLng.lng() } });
  };

  return (
    <div className="map-places-container">
      {editable && (
        <PlacesAutocomplete
          setCenter={selectCenter}
          setSelected={setSelected}
          selected={isSelected}
          mapAddress={addressText}
        />
      )}

      <GoogleMap
        zoom={zoom}
        center={center}
        options={editable ? EDIT_MAP_OPTIONS : VIEW_MAP_OPTIONS}
        mapContainerClassName="map-container"
        onClick={editable && onMapClick}
      >
        {editable && props.currentLocationAllowed && (
          <HexLocationButton onLocate={onLocate} />
        )}

        {distance && <HexGoogleDistance destination={initialViewPort}/>}
        {!distance && <Marker position={center} draggable={false} />}
      </GoogleMap>
    </div>
  );
}
